import React, { useEffect, useRef, useState } from "react";
import {
  BlockStack,
  Button,
  Card,
  InlineGrid,
  Text,
  TextField,
  Select,
} from "@shopify/polaris";
import { XIcon } from "@shopify/polaris-icons";
import supabase from "../../../supabase-client";
import moment from "moment";
import PaymentAllocationModal from "../../Invoicing/PaymentAllocationModal";
import { getCreditBalance } from "../../../utility/clientCredit";

function AddPayment({
  activated,
  setActivated,
  deposit,
  reservation,
  customer,
  company,
  onPaymentSaved,
}) {
  // Clip (4) is gone from the card methods on purpose: a Clip charge records
  // itself through the webhook and the transaction sweep, so entering it here
  // too books the same money twice. Terminal Afirme (5) stays until card volume
  // has actually moved to Clip. Fintoc was never offered here.
  const paymentMethods = [
    { name: "Efectivo", accounts: [1] },
    { name: "Tarjeta de Crédito", accounts: [5] },
    { name: "Tarjeta de Débito", accounts: [5] },
    { name: "Transferencia", accounts: [2, 3] },
    { name: "Depósito en Efectivo", accounts: [2, 3] },
  ];

  const [transactionAccounts, setTransactionAccounts] = useState(undefined);
  const [transactionInfo, setTransactionInfo] = useState({
    method: "Efectivo",
    account: undefined,
    amount: undefined,
    date: undefined,
    card_last4: "",
  });

  // Allocation modal state
  const [allocationModal, setAllocationModal] = useState({
    open: false,
    transactionId: null,
    amount: 0,
  });

  // Synchronous re-entry guard. React state batching can let a fast second
  // click slip past a `disabled` flag derived from useState — a ref flips
  // immediately and blocks the duplicate.
  const savingRef = useRef(false);

  useEffect(() => {
    async function fetchTransactionAccounts() {
      const { data, error } = await supabase
        .from("transaction_accounts")
        .select("id, name")
        .eq("manual_entry", true)
        .order("order", { ascending: true });
      if (error) {
        console.error(error.message);
        return;
      }
      const account = paymentMethods.find(
        (m) => m.name === transactionInfo.method
      ).accounts[0];
      const today = moment().format("YYYY-MM-DD");
      setTransactionInfo({ ...transactionInfo, account: account, date: today });
      setTransactionAccounts(data);
    }
    if (transactionAccounts === undefined && activated)
      fetchTransactionAccounts();
  });

  if (!activated && !allocationModal.open) return <></>;

  const handleClose = () => {
    setTransactionAccounts(undefined);
    setTransactionInfo({
      method: "Efectivo",
      account: undefined,
      amount: undefined,
      date: undefined,
      card_last4: "",
    });
    setActivated(false);
  };

  const handlePaymentMethodChange = (e) => {
    const account = paymentMethods.find((m) => m.name === e).accounts[0];
    setTransactionInfo({ ...transactionInfo, method: e, account: account });
  };

  const getAvailableAccounts = () => {
    const paymentMethod = paymentMethods.find(
      (m) => m.name === transactionInfo.method
    );
    return (transactionAccounts || []).filter(
      (acc) => paymentMethod.accounts.includes(acc.id) === true
    );
  };

  const getAccountOptions = () => {
    return getAvailableAccounts().map((account) => ({
      label: account.name,
      value: account.id,
    }));
  };

  const handleAccountChange = (e) => {
    setTransactionInfo({ ...transactionInfo, account: parseInt(e) });
  };

  const handleAmountChange = (e) => {
    setTransactionInfo({ ...transactionInfo, amount: parseFloat(e) });
  };

  const handleDateChange = (e) => {
    setTransactionInfo({ ...transactionInfo, date: e });
  };

  // Determine billing entity
  const billingEntityType = company ? "company" : "customer";
  const billingEntityId = company || customer;

  const handleTransactionSave = async () => {
    if (savingRef.current) return;
    savingRef.current = true;

    try {
      // Defense against double-entry: if a matching transaction was created
      // in the last 2 minutes (same reservation/customer/company + same
      // amount + same account + same isDeposit flag), warn before inserting.
      // Catches double-clicks that survived the savingRef (modal closed and
      // reopened, or different staff entering the same payment back-to-back).
      const recentCutoff = new Date(Date.now() - 2 * 60 * 1000).toISOString();
      let dupQuery = supabase
        .from("transactions")
        .select("id, amount, created_at, payment_date")
        .eq("type", "income")
        .eq("isDeposit", deposit)
        .eq("amount", transactionInfo.amount)
        .eq("account", transactionInfo.account)
        .gte("created_at", recentCutoff)
        .limit(1);
      if (reservation) dupQuery = dupQuery.eq("reservation", reservation);
      else if (customer) dupQuery = dupQuery.eq("customer", customer);
      else if (company) dupQuery = dupQuery.eq("billing_entity_id", company).eq("billing_entity_type", "company");
      const { data: existing } = await dupQuery;
      if (existing && existing.length > 0) {
        const e = existing[0];
        const ago = Math.max(1, Math.round((Date.now() - new Date(e.created_at).getTime()) / 1000));
        const ok = window.confirm(
          `Ya se registró un ${deposit ? "depósito" : "pago"} de $${transactionInfo.amount} hace ${ago}s en esta ${reservation ? "reservación" : "cuenta"}. ¿Registrar otro de todos modos?`
        );
        if (!ok) return;
      }

      // A deposit carried over from a finished reservation must be moved via
      // "Aplicar a garantía" (credit ledger), not re-entered here as fresh bank
      // income — the money only ever entered Caja once. Recording it again on a
      // real account double-counts it: the source reservation still has to be
      // returned/credited to clear its completion blocker, minting phantom
      // credit and breaking bank reconciliation.
      if (deposit && billingEntityId) {
        const credit = await getCreditBalance(billingEntityType, billingEntityId);
        if (credit >= Number(transactionInfo.amount) - 0.01) {
          const ok = window.confirm(
            `Este cliente tiene $${credit.toLocaleString("es-MX")} de crédito disponible.\n\n` +
              `Si es el depósito de otra reservación, NO lo registres aquí: cierra este formulario y usa "Aplicar a garantía" en la tarjeta de crédito del cliente. Registrarlo como pago nuevo duplica el dinero.\n\n` +
              `¿Es dinero nuevo que el cliente acaba de pagar?`
          );
          if (!ok) return;
        }
      }

      // Derive card_type from the selected payment method
      let cardType = null;
      if (transactionInfo.method === "Tarjeta de Crédito") cardType = "credit";
      else if (transactionInfo.method === "Tarjeta de Débito") cardType = "debit";

      const { data: txn, error } = await supabase
        .from("transactions")
        .insert({
          type: "income",
          amount: transactionInfo.amount,
          reservation: reservation,
          customer: customer,
          isDeposit: deposit,
          account: transactionInfo.account,
          card_type: cardType,
          card_last4: cardType ? (transactionInfo.card_last4 || null) : null,
          payment_date: transactionInfo.date || null,
          // Deposits are collateral, not revenue — never conciliated or invoiced
          unallocated_amount: deposit ? 0 : transactionInfo.amount,
          billing_entity_type: billingEntityType,
          billing_entity_id: billingEntityId,
          invoiced: deposit ? true : false,
        })
        .select("id")
        .single();

      if (error) {
        console.error(error);
        alert("Error: " + error.message);
        return;
      }

      // reservations.paid / balance / fully_paid are owned by DB triggers
      // (see 20260515_reservation_payment_ledger.sql). The transaction
      // insert above fires trg_txn_recompute, which derives those columns
      // from allocations + direct unallocated income − refunds. Don't
      // touch them from JS — it'd race the trigger.

      // Close payment form
      handleClose();

      // Open allocation modal (for non-deposit payments)
      if (!deposit && billingEntityId) {
        setAllocationModal({
          open: true,
          transactionId: txn.id,
          amount: transactionInfo.amount,
        });
      } else if (onPaymentSaved) {
        onPaymentSaved();
      }
    } finally {
      savingRef.current = false;
    }
  };

  return (
    <>
      {/* Payment allocation modal */}
      <PaymentAllocationModal
        open={allocationModal.open}
        onClose={() => {
          setAllocationModal({ open: false, transactionId: null, amount: 0 });
          if (onPaymentSaved) onPaymentSaved();
        }}
        transactionId={allocationModal.transactionId}
        paymentAmount={allocationModal.amount}
        billingEntityType={billingEntityType}
        billingEntityId={billingEntityId}
        reservationId={reservation || null}
        onComplete={() => {
          if (onPaymentSaved) onPaymentSaved();
        }}
      />

      {/* Payment form */}
      {activated && transactionAccounts && (
        <>
          <div className="full_screen_blur_bg" onClick={handleClose} />
          <div className="popup_window">
            <Card>
              <InlineGrid columns="1fr auto">
                <div style={{ marginBottom: "0.5rem" }}>
                  <Text variant="headingMd">
                    Agregar {deposit ? "Depósito" : "Pago"}
                  </Text>
                </div>
                <Button variant="plain" icon={XIcon} onClick={handleClose} />
              </InlineGrid>
              <BlockStack gap={400}>
                <InlineGrid columns={2} gap={200}>
                  <Select
                    label="Método de Pago"
                    options={paymentMethods.map((method) => method.name)}
                    onChange={handlePaymentMethodChange}
                    value={transactionInfo.method}
                  />
                  <Select
                    label="Cuenta"
                    options={getAccountOptions()}
                    value={transactionInfo.account}
                    onChange={handleAccountChange}
                  />
                </InlineGrid>
                <InlineGrid columns={2} gap={200}>
                  <TextField
                    type="date"
                    value={transactionInfo.date}
                    onChange={handleDateChange}
                  />
                  <TextField
                    type="number"
                    prefix="$"
                    value={transactionInfo.amount}
                    onChange={handleAmountChange}
                  />
                </InlineGrid>
                {(transactionInfo.method === "Tarjeta de Crédito" ||
                  transactionInfo.method === "Tarjeta de Débito") && (
                  <TextField
                    label="Últimos 4 dígitos de la tarjeta"
                    type="text"
                    inputMode="numeric"
                    maxLength={4}
                    value={transactionInfo.card_last4}
                    onChange={(e) =>
                      setTransactionInfo({
                        ...transactionInfo,
                        card_last4: e.replace(/\D/g, "").slice(0, 4),
                      })
                    }
                    error={
                      transactionInfo.card_last4 &&
                      !/^\d{4}$/.test(transactionInfo.card_last4)
                        ? "4 dígitos"
                        : undefined
                    }
                    helpText="Requerido para conciliación de tarjeta"
                    autoComplete="off"
                  />
                )}
                <Button
                  disabled={
                    transactionInfo.amount === undefined ||
                    ((transactionInfo.method === "Tarjeta de Crédito" ||
                      transactionInfo.method === "Tarjeta de Débito") &&
                      !/^\d{4}$/.test(transactionInfo.card_last4 || ""))
                  }
                  onClick={handleTransactionSave}
                >
                  Guardar
                </Button>
              </BlockStack>
            </Card>
          </div>
        </>
      )}
    </>
  );
}

export default AddPayment;
